import type {
  EvaluatorKind,
  Leaderboard,
  LeaderboardRow,
  MetaDataset,
  MetaDatasetEntry,
  MetaDatasetPayload,
  MetricBlock,
  MetricDirection,
  RunStatus,
  SampleStats,
  SplitEntry,
  SplitPolicy,
  SubmissionStatus,
} from '../types/leaderboard';
import { usageTotalCost, usageTotalTokens } from './usageSummary';

/**
 * Правила лидерборда, которые нельзя держать в разметке.
 *
 * Три вещи здесь стоят отдельного внимания, потому что если их перепутать,
 * интерфейс будет врать, а не просто выглядеть иначе:
 *
 *   • `incompatible` — не поломка. Алгоритм не подошёл бенчмарку и до счёта не
 *     дошёл. Одна плашка с `failed` заставит человека думать, что лидерборд сломан.
 *   • Строки таблицы уже отсортированы бэкендом с учётом направления метрики.
 *     Своя сортировка по убыванию при `minimize` перевернёт таблицу вверх ногами.
 *   • Счёт без `sample_stats` бессмысленен: 0.71 по 95 примерам из 100 и 0.71 по
 *     40 из 100 — разные утверждения.
 */

// ─── Статусы сабмишена ───────────────────────────────────────────────────────

export type StatusTone = 'neutral' | 'progress' | 'ok' | 'warn' | 'danger';

export interface StatusMeta {
  label: string;
  tone: StatusTone;
  /** Уточнение под плашкой там, где короткого слова не хватает. */
  hint?: string;
}

export const SUBMISSION_STATUS_META: Record<SubmissionStatus, StatusMeta> = {
  queued: { label: 'In queue', tone: 'neutral' },
  running: { label: 'Running', tone: 'progress' },
  succeeded: { label: 'Scored', tone: 'ok' },
  failed: { label: 'Failed', tone: 'danger', hint: 'Failed while running' },
  timed_out: { label: 'Timed out', tone: 'danger' },
  // Отдельный тон: это отказ бенчмарка, а не падение алгоритма.
  incompatible: { label: 'Incompatible', tone: 'warn', hint: 'Not run: does not fit this benchmark' },
  cancelling: { label: 'Cancelling', tone: 'progress' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

export const RUN_STATUS_META: Record<RunStatus, StatusMeta> = {
  queued: { label: 'In queue', tone: 'neutral' },
  running: { label: 'Running', tone: 'progress' },
  succeeded: { label: 'Done', tone: 'ok' },
  // Прогон дал результат, но неполный — ни зелёный, ни красный.
  partially_succeeded: { label: 'Partly done', tone: 'warn', hint: 'Some algorithms did not score' },
  failed: { label: 'Failed', tone: 'danger' },
  timeout: { label: 'Timed out', tone: 'danger' },
  cancelling: { label: 'Cancelling', tone: 'progress' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function submissionStatusMeta(status: string): StatusMeta {
  return SUBMISSION_STATUS_META[status as SubmissionStatus] ?? { label: status, tone: 'neutral' };
}

export function runStatusMeta(status: string): StatusMeta {
  return RUN_STATUS_META[status as RunStatus] ?? { label: status, tone: 'neutral' };
}

/** Прогон ещё идёт — по этому решается, продолжать ли опрос. */
export function isSubmissionActive(status: string): boolean {
  return status === 'queued' || status === 'running' || status === 'cancelling';
}

// ─── Эвалюаторы ──────────────────────────────────────────────────────────────

export interface EvaluatorDefinition {
  kind: EvaluatorKind;
  label: string;
  /** Поле в `evaluator_config`, без которого бэкенд ответит 400. */
  requires?: 'prompt' | 'artifact_path';
  hint?: string;
}

/**
 * Восемь видов. `bert_score` — единственный через подчёркивание, остальные
 * составные через дефис; поэтому выбор списком, а не вводом строки.
 */
export const EVALUATORS: EvaluatorDefinition[] = [
  { kind: 'algorithmic', label: 'Algorithmic' },
  { kind: 'similarity', label: 'Similarity' },
  { kind: 'bert_score', label: 'BERTScore' },
  { kind: 'openai-similarity', label: 'OpenAI similarity' },
  { kind: 'virtual-user-engagement', label: 'Virtual user engagement' },
  { kind: 'llm-judge', label: 'LLM judge', requires: 'prompt', hint: 'Scoring prompt is required' },
  { kind: 'message-scorer', label: 'Message scorer', requires: 'artifact_path', hint: 'Artifact path is required' },
  { kind: 'config-scorer', label: 'Config scorer', requires: 'artifact_path', hint: 'Artifact path is required' },
];

export function evaluatorDefinition(kind: string): EvaluatorDefinition | undefined {
  return EVALUATORS.find((e) => e.kind === kind);
}

// ─── Путь первичной метрики ──────────────────────────────────────────────────

export const METRIC_BLOCKS: Array<{ block: MetricBlock; label: string }> = [
  { block: 'validation', label: 'Validation' },
  { block: 'test_initial', label: 'Test — initial' },
  { block: 'test_final', label: 'Test — final' },
  { block: 'test_delta', label: 'Test — delta' },
];

export const DEFAULT_METRIC_PATH = 'validation.score';

export interface ParsedMetricPath {
  block: string;
  metric: string;
  /** Блок из разрешённого набора. `train` сюда не входит намеренно. */
  blockKnown: boolean;
}

/** `<блок>.<метрика>` — путь, а не имя метрики: `test_final.objectives.helpfulness` тоже валиден. */
export function parseMetricPath(path: string): ParsedMetricPath | null {
  const trimmed = (path || '').trim();
  const dot = trimmed.indexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return null;
  const block = trimmed.slice(0, dot);
  const metric = trimmed.slice(dot + 1);
  if (!metric.trim()) return null;
  return { block, metric, blockKnown: METRIC_BLOCKS.some((b) => b.block === block) };
}

export function formatMetricPath(block: MetricBlock, metric: string): string {
  return `${block}.${(metric || '').trim()}`;
}

/** Метрика опирается на тестовую часть — значит без неё лидерборд останется пустым навсегда. */
export function metricNeedsTest(path: string): boolean {
  const parsed = parseMetricPath(path);
  return !!parsed && parsed.block.startsWith('test');
}

// ─── Slug ────────────────────────────────────────────────────────────────────

/** Уезжает в ML и попадает в пути файлов, поэтому набор символов узкий. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export function isValidSlug(slug: string): boolean {
  return SLUG_PATTERN.test((slug || '').trim());
}

// ─── Валидация формы ─────────────────────────────────────────────────────────

export interface ValidationIssue {
  field: string;
  message: string;
}

export interface MetaDatasetDraft {
  slug: string;
  name: string;
  /** Интеграция, из каталога которой выбраны датасеты. */
  integrationId?: string;
  modelPrompt: string;
  modelName: string;
  modelTemperature?: number;
  /** Плоский список (BE-5): ML сам режет каждый датасет на train/validation/test. */
  datasets: MetaDatasetEntry[];
  /** Один оценщик на весь мета-датасет — не на пару (dataset, сплит), как раньше. */
  evaluatorKind: EvaluatorKind;
  evaluatorConfig: Record<string, unknown>;
  metricPath: string;
  metricDirection: MetricDirection;
  timeoutS?: number;
  maxIterations?: number;
  /** Сколько алгоритмов считать одновременно. */
  maxParallelism?: number;
  /** Сколько запросов к модели вести одновременно внутри одного алгоритма. */
  parallelCalls?: number;
  /**
   * Только для записей старой формы разделов — переносится из `initialData` как
   * есть, форма его не показывает и не редактирует. Не пробросить значило бы
   * молча обнулить seed и доли у существующей записи при любой правке через
   * новую форму: полей для них тут больше нет, и потерю не было бы видно.
   */
  splitPolicy?: SplitPolicy;
}

/**
 * Повторяет проверки бэкенда, чтобы человек узнал о них до отправки. Серверные
 * сообщения всё равно остаются главными — их показываем как есть, они точнее.
 */
export function validateMetaDataset(draft: MetaDatasetDraft): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  if (!draft.name.trim()) issues.push({ field: 'name', message: 'Name is required.' });

  if (!draft.slug.trim()) {
    issues.push({ field: 'slug', message: 'Slug is required.' });
  } else if (!isValidSlug(draft.slug)) {
    issues.push({ field: 'slug', message: 'Slug takes lowercase letters, digits, "_" and "-" only — it ends up in ML file paths.' });
  }

  if (!draft.modelPrompt.trim()) issues.push({ field: 'modelPrompt', message: 'Model prompt is required.' });
  if (!draft.modelName.trim()) issues.push({ field: 'modelName', message: 'OpenAI model is required.' });
  if (draft.maxParallelism !== undefined && (!Number.isInteger(draft.maxParallelism) || draft.maxParallelism < 1)) {
    issues.push({ field: 'maxParallelism', message: 'Parallel algorithms must be a whole number of at least 1.' });
  }
  if (draft.parallelCalls !== undefined && (!Number.isInteger(draft.parallelCalls) || draft.parallelCalls < 1)) {
    issues.push({ field: 'parallelCalls', message: 'Parallel calls must be a whole number of at least 1.' });
  }
  if (draft.modelTemperature === undefined || !Number.isFinite(draft.modelTemperature)) {
    issues.push({ field: 'modelTemperature', message: 'Model temperature must be a number.' });
  }

  if (draft.datasets.length === 0) {
    issues.push({ field: 'datasets', message: 'Add at least one dataset — ML splits it into train, validation and test itself.' });
  }
  draft.datasets.forEach((entry, i) => {
    if (!entry.dataset_id) {
      issues.push({ field: `datasets[${i}]`, message: `Dataset row ${i + 1} is empty — pick a dataset or remove the row.` });
    }
  });

  const def = evaluatorDefinition(draft.evaluatorKind);
  if (!def) {
    issues.push({
      field: 'evaluatorKind',
      message: `Unknown evaluator "${draft.evaluatorKind}". Allowed: ${EVALUATORS.map((e) => e.kind).join(', ')}.`,
    });
  } else if (def.requires) {
    const value = draft.evaluatorConfig?.[def.requires];
    if (typeof value !== 'string' || !value.trim()) {
      issues.push({ field: `evaluatorConfig.${def.requires}`, message: `"${def.label}" requires ${def.requires}.` });
    }
  }

  const parsed = parseMetricPath(draft.metricPath);
  if (!parsed) {
    issues.push({ field: 'metricPath', message: 'Primary metric is a path: "<block>.<metric>", for example validation.score.' });
  } else if (parsed.block === 'train') {
    issues.push({ field: 'metricPath', message: 'Ranking by train is not allowed — that is the split the algorithm was tuned on.' });
  } else if (!parsed.blockKnown) {
    issues.push({
      field: 'metricPath',
      message: `Unknown metric block "${parsed.block}". Allowed: ${METRIC_BLOCKS.map((b) => b.block).join(', ')}.`,
    });
  } else if (parsed.block.startsWith('test') && draft.datasets.length === 0) {
    issues.push({
      field: 'metricPath',
      message: 'Primary metric points at test, but there is no dataset yet — such a leaderboard would stay empty forever.',
    });
  }

  return issues;
}

export function buildMetaDatasetPayload(draft: MetaDatasetDraft): MetaDatasetPayload {
  const budget: Record<string, number> = {};
  // Ноль здесь — незаполненное поле, а не выбор: не отправляем.
  if (draft.timeoutS) budget.timeout_s = draft.timeoutS;
  if (draft.maxIterations) budget.max_iterations = draft.maxIterations;

  return {
    slug: draft.slug.trim(),
    name: draft.name.trim(),
    // Уезжает на бэкенд, потому что каталог датасетов поклиентский: по нему
    // идентификатор выбранной записи разрешается в имя объекта в S3. Без него
    // ML получал UUID и не находил датасет, который лежит под своей меткой.
    ...(draft.integrationId?.trim() ? { integration_id: draft.integrationId.trim() } : {}),
    datasets: draft.datasets,
    evaluator: { kind: draft.evaluatorKind, ...draft.evaluatorConfig },
    ...(draft.splitPolicy ? { split_policy: draft.splitPolicy } : {}),
    leaderboard: {
      primary_metric_path: draft.metricPath.trim() || DEFAULT_METRIC_PATH,
      metric_direction: draft.metricDirection,
      ...(draft.maxParallelism ? { max_parallelism: draft.maxParallelism } : {}),
      ...(draft.parallelCalls ? { parallel_calls: draft.parallelCalls } : {}),
      model_config: {
        kind: 'openai',
        config: {
          prompt: draft.modelPrompt.trim(),
          model: draft.modelName.trim(),
          temperature: draft.modelTemperature ?? 0,
        },
      },
      ...(Object.keys(budget).length ? { budget } : {}),
    },
  };
}

/**
 * Что изменится, если запись старой формы разделов сохранить как один плоский
 * список с одним оценщиком (BE-5). Дробление на train/validation/test само по
 * себе — намеренная нормализация, спорить не с чем. Но если разделы раньше
 * несли РАЗНОЕ (свой оценщик у каждого, датасет только в части сплитов),
 * сохранение через новую форму молча это стирает — предупредить нужно
 * заранее, до сохранения, а не постфактум.
 */
export function metaDatasetNormalizationWarnings(data: MetaDataset | undefined): string[] {
  if (!data) return [];
  const splits = (
    [
      ['train', data.train],
      ['validation', data.validation],
      ['test', data.test],
    ] as const
  )
    .map(([name, items]) => ({ name, items: Array.isArray(items) ? items : [] }))
    .filter((s) => s.items.length > 0);
  if (splits.length === 0) return [];

  const allItems = splits.flatMap((s) => s.items);
  const chosen = allItems[0];
  const warnings: string[] = [];

  const sameEvaluator = (a: SplitEntry, b: SplitEntry) =>
    a.evaluator_kind === b.evaluator_kind
    && JSON.stringify(a.evaluator_config ?? {}) === JSON.stringify(b.evaluator_config ?? {});
  if (allItems.some((item) => !sameEvaluator(item, chosen))) {
    warnings.push(
      `Splits currently use different evaluators. Saving keeps only one for the whole meta-dataset — ` +
      `"${chosen.evaluator_kind}"${Object.keys(chosen.evaluator_config ?? {}).length ? ` (${JSON.stringify(chosen.evaluator_config)})` : ''} ` +
      `— settings of the other evaluator(s) will be lost.`
    );
  }

  const idsBySplit = splits.map((s) => new Set(s.items.map((i) => i.dataset_id).filter(Boolean)));
  const allIds = Array.from(new Set(idsBySplit.flatMap((set) => Array.from(set))));
  const partial = allIds.filter((id) => idsBySplit.some((set) => !set.has(id)));
  if (partial.length > 0) {
    const names = (id: string) => splits.filter((_, i) => idsBySplit[i].has(id)).map((s) => s.name).join('+');
    warnings.push(
      `${partial.length === 1 ? 'Dataset' : 'Datasets'} ${partial.map((id) => `${id} (currently ${names(id)})`).join(', ')} ` +
      `${partial.length === 1 ? 'is' : 'are'} not in every split. Saving adds ${partial.length === 1 ? 'it' : 'them'} to all three ` +
      `(train, validation and test) — ML now cuts every dataset the same way.`
    );
  }

  return warnings;
}

// ─── Покрытие выборки ────────────────────────────────────────────────────────

export interface Coverage {
  scored: number;
  total: number;
  ratio: number | null;
  /** Меньше 90% примеров дошло до оценки — счёт стоит читать с оговоркой. */
  thin: boolean;
}

export function sampleCoverage(stats?: SampleStats | null): Coverage | null {
  if (!stats || !stats.total) return null;
  const total = stats.total;
  const scored = stats.succeeded ?? 0;
  const ratio = total > 0 ? scored / total : null;
  return { scored, total, ratio, thin: ratio !== null && ratio < 0.9 };
}

export function totalErrors(stats?: { algorithm_errors?: number; evaluator_errors?: number; timeouts?: number } | null): number {
  if (!stats) return 0;
  return (stats.algorithm_errors ?? 0) + (stats.evaluator_errors ?? 0) + (stats.timeouts ?? 0);
}

// ─── Прочее ──────────────────────────────────────────────────────────────────

/** Защищает от двойного клика и от ретрая: повтор с тем же ключом вернёт тот же прогон. */
export function newIdempotencyKey(): string {
  const c = typeof crypto !== 'undefined' ? (crypto as Crypto & { randomUUID?: () => string }) : undefined;
  if (c?.randomUUID) return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function formatScore(value?: number | null, digits = 4): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
}

/**
 * `значение ± σ (n)` — n — сколько прогонов из строки успели досчитаться.
 * `stddev` опущен намеренно при `null`, а не показан как `± 0`: один прогон не
 * имеет разброса, а ноль читался бы как «идеально стабильно».
 */
export function formatTestScoreSpread(
  mean: number | null | undefined,
  stddev: number | null | undefined,
  succeededCount: number | null | undefined
): string {
  if (typeof mean !== 'number' || !Number.isFinite(mean)) return '—';
  const spread = typeof stddev === 'number' && Number.isFinite(stddev) ? ` ± ${stddev.toFixed(4)}` : '';
  // Ноль успешных прогонов рядом с числом — это не "ноль", это отсутствие
  // счётчика: значение тогда взято не из этих прогонов, а из best_submission.
  const count = typeof succeededCount === 'number' && succeededCount > 0 ? ` (${succeededCount})` : '';
  return `${formatScore(mean)}${spread}${count}`;
}

/**
 * Разброс серии подписью под значением строки: `avg 0.7100 ± 0.0500 (5)`.
 *
 * Строка таблицы описывает ОДИН прогон — выбранный по валидации, — и все её
 * числа, включая Test Δ, взяты из него. Среднее по серии стояло на месте
 * самого значения Test, и дельта рядом считалась от другого числа: в одной
 * строке два источника. Здесь среднее уходит на вторую строку как
 * характеристика серии, а не прогона.
 *
 * `null`, когда подписывать нечего: серии нет (меньше двух успешных прогонов)
 * или среднее не приехало.
 */
export function formatRunSpread(
  mean: number | null | undefined,
  stddev: number | null | undefined,
  succeededCount: number | null | undefined
): string | null {
  if (typeof mean !== 'number' || !Number.isFinite(mean)) return null;
  if (typeof succeededCount !== 'number' || succeededCount < 2) return null;
  const spread = typeof stddev === 'number' && Number.isFinite(stddev) ? ` ± ${stddev.toFixed(4)}` : '';
  return `avg ${formatScore(mean)}${spread} (${succeededCount})`;
}

/**
 * Куда смотрит дельта — вверх или вниз, — решает направление метрики, а не знак.
 *
 * Красный на любом минусе врал ровно там, где табло построено на `minimize`:
 * `validation.loss` упавший с 0.19 до 0.15 — это лучший результат прогона, а
 * подписан он был как ухудшение. Направление известно только табло, поэтому
 * оно и приходит аргументом; без него тон не назначается вовсе — выдумывать
 * знак хуже, чем оставить число нейтральным.
 */
export type DeltaTone = 'good' | 'bad' | 'flat' | null;

export function deltaTone(value: number | null | undefined, direction?: MetricDirection): DeltaTone {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value === 0) return 'flat';
  if (direction !== 'maximize' && direction !== 'minimize') return null;
  const improved = direction === 'maximize' ? value > 0 : value < 0;
  return improved ? 'good' : 'bad';
}

/** Класс для ячейки дельты. Пустая строка — намеренно нейтральное значение. */
export function deltaToneClass(value: number | null | undefined, direction?: MetricDirection): string {
  const tone = deltaTone(value, direction);
  return tone === 'good' ? 'lb-delta--good' : tone === 'bad' ? 'lb-delta--bad' : '';
}

export function formatDuration(seconds?: number | null): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

/**
 * Итог токенов сабмишена. Сводка вложенная, поэтому читаем её разбором, а не по
 * плоскому `total_tokens`: такого поля на проводе нет и колонка была пустой.
 * `statusHint` — авторитетный `usage_status` сабмишена, когда он есть.
 */
export function formatUsageTokens(usage: unknown, statusHint?: string | null): string {
  const tokens = usageTotalTokens(usage, statusHint);
  return tokens == null ? '—' : tokens.toLocaleString();
}

export function formatUsageCost(usage: unknown, statusHint?: string | null): string {
  const cost = usageTotalCost(usage, statusHint);
  if (cost == null) return '—';
  if (cost === 0) return '$0.00';
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(2)}`;
}

// ─── Сортировка таблицы (FE-3) ───────────────────────────────────────────────

/**
 * Колонки, по которым можно кликом переставить строки. Порядок по умолчанию —
 * от бэкенда, с учётом направления первичной метрики; своя сортировка
 * включается только явным кликом по заголовку.
 */
export type SortableColumn = 'train' | 'validation' | 'test' | 'usage' | 'coefficient' | 'algoCoefficient';

/**
 * Оба коэффициента — числа, которым можно верить, только при `usage_status ===
 * 'known'`. `partial` значит, что часть расхода не измерена, и показывать
 * прочерком надо оба, а не только тот, что подсчитан руками бэкенда.
 * Статус не пришёл вовсе (старый бэкенд без этого поля) — не гейтим, ведём
 * себя как раньше.
 */
function usableCoefficient(value: number | null | undefined, usageStatus?: string | null): number | null {
  if (usageStatus != null && usageStatus !== 'known') return null;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function formatUsageCoefficient(value: number | null | undefined, usageStatus?: string | null): string {
  const usable = usableCoefficient(value, usageStatus);
  return usable === null ? '—' : formatScore(usable, 2);
}

export function sortColumnValue(row: LeaderboardRow, column: SortableColumn): number | null {
  const sub = row.best_submission;
  switch (column) {
    case 'train': return typeof sub?.train_score === 'number' ? sub.train_score : null;
    case 'validation': return typeof sub?.validation_score === 'number' ? sub.validation_score : null;
    case 'test':
      // Сортируем по тому же числу, что стоит в ячейке, — по тестовому скору
      // выбранного прогона. Среднее по серии ушло подписью под ним и строку
      // не ранжирует: иначе порядок считался бы не по видимому значению.
      return typeof sub?.test_final_score === 'number' ? sub.test_final_score : null;
    case 'usage': return usageTotalTokens(sub?.usage_totals, sub?.usage_status);
    case 'coefficient': return usableCoefficient(sub?.usage_coefficient, sub?.usage_status);
    case 'algoCoefficient': return usableCoefficient(sub?.usage_coefficient_algorithm, sub?.usage_status);
    default: return null;
  }
}

/** Строк без значения не бывает первыми ни по возрастанию, ни по убыванию —
 *  иначе «нет данных» выглядело бы как «лучший результат». */
export function compareNullableNumbers(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return (a - b) * dir;
}

export function sortLeaderboardRows(
  rows: LeaderboardRow[],
  sort: { column: SortableColumn; dir: 'asc' | 'desc' } | null
): LeaderboardRow[] {
  if (!sort) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => compareNullableNumbers(sortColumnValue(a, sort.column), sortColumnValue(b, sort.column), dir));
}

/**
 * Место строки в порядке бекенда — не в отображаемом. Своя сортировка колонки
 * переставляет строки на экране, но ранг и медаль (`#1..#3`) должны остаться
 * там, где их дал бекенд: иначе клик по «Coefficient» ставит на первое место
 * с золотой плашкой самый расточительный алгоритм, а подиум выше по-прежнему
 * построен по бекендовому порядку — экран противоречит сам себе.
 */
export function rankByAlgorithmId(rows: LeaderboardRow[]): Map<string, number> {
  const map = new Map<string, number>();
  rows.forEach((row, i) => map.set(row.algorithm.id, i + 1));
  return map;
}

// ─── Общие табло (AIT-246) ───────────────────────────────────────────────────

/**
 * Табло стало одно на стенд: в списках и в таблице приезжает чужое. Владение
 * решает две вещи — показывать ли «Cancel» и как подписывать строку.
 *
 * Неизвестный автор (поле не пришло) — это НЕ «моё». Иначе кнопка отмены
 * появилась бы на чужом прогоне и упёрлась бы в 403 уже после клика.
 */
export function isOwnedBy(entity: { user_id?: string } | null | undefined, currentUserId?: string | null): boolean {
  if (!entity?.user_id || !currentUserId) return false;
  return entity.user_id === currentUserId;
}

/**
 * Подпись автора строки. Своё называем явно, чужое — идентификатором, потому
 * что имён пользователей этот контракт не отдаёт.
 */
export function authorLabel(
  entity: { user_id?: string } | null | undefined,
  currentUserId?: string | null
): string | null {
  if (!entity?.user_id) return null;
  return isOwnedBy(entity, currentUserId) ? 'you' : entity.user_id;
}

/**
 * До AIT-246 чужой идентификатор давал 404 — строка просто не находилась.
 * Теперь она находится, но действие запрещено. Смешивать эти случаи нельзя:
 * «нет такого прогона» и «прогон не ваш» чинятся по-разному.
 */
export function accessErrorText(status: number | undefined, fallback: string): string | null {
  if (status === 403) return 'This belongs to another user, so you cannot change it.';
  if (status === 404) return 'It no longer exists — the list may be out of date.';
  return status ? fallback : null;
}

// ─── Версии мета-датасета (AIT-318) ──────────────────────────────────────────

/**
 * Правка мета-датасета может форкнуться в `foo_v1`, `foo_v2`… Родителя как
 * поля нет и не будет — группа читается из самого слага.
 */
const VERSION_SUFFIX = /^(.+)_v(\d+)$/;

export function parseLeaderboardVersion(slug: string): { baseSlug: string; version: number } {
  const trimmed = slug || '';
  const match = VERSION_SUFFIX.exec(trimmed);
  return match ? { baseSlug: match[1], version: Number(match[2]) } : { baseSlug: trimmed, version: 0 };
}

export interface LeaderboardVersionGroup<T = Leaderboard> {
  baseSlug: string;
  /** Слаг без версии, если он есть в списке, иначе самая старая из версий. */
  head: T;
  /** Все элементы группы, включая `head`, по возрастанию номера версии. */
  versions: T[];
}

/**
 * Табло и мета-датасеты версионируются одним и тем же слагом, но носят его в
 * разных полях (`meta_dataset_slug` у табло, `slug` у мета-датасета) — раньше
 * группировка была зашита под табло, и список мета-датасетов, и переключатель
 * версий в шапке приходилось бы группировать заново своим кодом. Геттер слага
 * параметризован именно для того, чтобы вся группировка шла одной функцией.
 */
export function groupLeaderboardsByVersion<T extends { id: string }>(
  items: T[],
  getSlug: (item: T) => string | undefined = (item) => (item as unknown as { meta_dataset_slug?: string }).meta_dataset_slug
): LeaderboardVersionGroup<T>[] {
  const order: string[] = [];
  const byBase = new Map<string, T[]>();
  items.forEach((item) => {
    const { baseSlug } = parseLeaderboardVersion(getSlug(item) || item.id);
    if (!byBase.has(baseSlug)) {
      order.push(baseSlug);
      byBase.set(baseSlug, []);
    }
    byBase.get(baseSlug)!.push(item);
  });
  return order.map((baseSlug) => {
    const versions = [...byBase.get(baseSlug)!].sort(
      (a, b) =>
        parseLeaderboardVersion(getSlug(a) || a.id).version
        - parseLeaderboardVersion(getSlug(b) || b.id).version
    );
    const head = versions.find((v) => parseLeaderboardVersion(getSlug(v) || v.id).version === 0) ?? versions[0];
    return { baseSlug, head, versions };
  });
}

/** Подпись версии в интерфейсе: базовый слаг — `base`, форки — `v1`, `v2`… */
export function versionLabel(version: number): string {
  return version > 0 ? `v${version}` : 'base';
}

// ─── Опорные строки (AIT-318) ────────────────────────────────────────────────

/**
 * Опорные алгоритмы только перефразируют промпт и служат уровнем шума, а не
 * участником соревнования. Раньше отличали по локализованному имени
 * (`Лидерборд бейзлайн …`) — переименование в каталоге ML молча снимало
 * плашку, а чужой сабмишен с похожим именем получал её незаслуженно.
 * `algorithm_id` вида `native:leaderboard_baseline2` — стабильный идентификатор,
 * имя на него не влияет.
 */
const REFERENCE_ALGORITHM_ID_PATTERN = /^native:leaderboard_baseline\d*$/i;

export function isReferenceAlgorithm(algorithmId?: string | null): boolean {
  return !!algorithmId && REFERENCE_ALGORITHM_ID_PATTERN.test(algorithmId.trim());
}
